import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { SafeUrl } from '@angular/platform-browser';
import { UserAvatarService } from '../../../../../../libs/shared/src/lib/services/user-avatar.service';

export type AvatarSize = 'sm' | 'md' | 'lg';

/**
 * Reusable avatar display: shows the photo when available, otherwise the user's
 * initials on a deterministic background color. When `editable`, exposes edit /
 * delete overlay actions (used on the profile page).
 */
@Component({
  selector: 'app-user-avatar',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
})
export class UserAvatarComponent {
  @Input() firstName?: string;
  @Input() lastName?: string;
  /** Sanitized object URL or `data:` URL string for the photo. */
  @Input() photo: SafeUrl | string | null = null;
  /** Stable id used to pick a consistent color (email or user id). */
  @Input() identifier?: string;
  @Input() size: AvatarSize = 'md';
  @Input() editable = false;
  /** Shows a spinner overlay while an upload is in flight. */
  @Input() loading = false;

  @Output() edit = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  constructor(private readonly userAvatarService: UserAvatarService) {}

  get initials(): string {
    return this.userAvatarService.getInitials(this.firstName, this.lastName) || '?';
  }

  get backgroundColor(): string {
    return this.userAvatarService.getAvatarColor(this.identifier || `${this.firstName}${this.lastName}`);
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    this.edit.emit();
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit();
  }
}
